import { faPause, faPenLine, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { ComponentProps, useState } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Tooltip, TooltipProps } from '@growtherapy/sprout-ui';

import { ControlPanelButton } from '../../components';
import { toast } from '../../toasts';

type ControlPanelButtonProps = Omit<
  ComponentProps<typeof ControlPanelButton>,
  'aria-label' | 'icon' | 'iconName' | 'iconVariant' | 'iconDefinition'
>;
type RecordingControlProps = ControlPanelButtonProps & {
  isRecording?: boolean;
  isRequestingConsent?: boolean;
  consentPending: boolean;
  onRecordingToggle: (shouldRecord: boolean) => void | Promise<void>;
  onPendingConsentClick?: () => void | Promise<void>;
  tooltipProps?: Partial<TooltipProps>;
};

export function RecordingControl({
  isRecording,
  isRequestingConsent,
  consentPending,
  onRecordingToggle,
  onPendingConsentClick,
  tooltipProps,
  ...props
}: RecordingControlProps) {
  const t = useTranslate();
  const [isLoading, setIsLoading] = useState(false);

  let buttonLabel = isRecording
    ? t('telehealth-core:recording.toggleRecordingOff')
    : t('telehealth-core:recording.toggleRecordingOn');
  let buttonIcon = isRecording ? faPause : faPlay;

  if (consentPending) {
    buttonLabel = t('telehealth-core:recording.requestTranscriptionConsent');
    buttonIcon = faPenLine;
  }

  if (isRequestingConsent) {
    buttonLabel = t('telehealth-core:recording.requestingClientConsent');
  }

  return (
    <Tooltip {...tooltipProps} text={tooltipProps?.text ?? buttonLabel} childIsInteractive>
      <ControlPanelButton
        {...props}
        loading={isLoading || isRequestingConsent}
        aria-label={buttonLabel}
        iconDefinition={buttonIcon}
        data-testid='recording-control'
        onClick={async () => {
          if (!consentPending) {
            try {
              setIsLoading(true);
              await onRecordingToggle(!isRecording);
            } catch (error) {
              toast.error(t('telehealth-core:recording.somethingWentWrongWhileTogglingRecording'));
            } finally {
              setIsLoading(false);
            }
          } else if (onPendingConsentClick) {
            await onPendingConsentClick();
          }
        }}
      />
    </Tooltip>
  );
}
